import { useId, useState } from 'react';
import { VAR_NAME_RE, type ConfigEntryDTO, type ConfigScope, type EnvironmentConfigDTO, type SetConfigRequest, type TargetDTO, type VpsDTO } from '@devrobot/shared';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { api, seg } from '../../../api/client';
import { qk, useApiMutation, useSharedValues } from '../../../api/queries';
import { ErrorAlert } from '../../../components/ErrorAlert';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toaster';
import { Haptic } from '../../../kit/haptics';
import { haptic } from '../../../kit/native';
import { ScopePicker, scopeValid } from './ScopePicker';

/** Set, replace or bind a configuration value in an explicitly chosen scope. */
export function SetValueModal({
  projectKey,
  cfg,
  entry,
  initialScope,
  vpsList,
  targets,
  onClose,
}: {
  projectKey: string;
  cfg: EnvironmentConfigDTO;
  /** Undefined when adding a variable that is not declared yet. */
  entry?: ConfigEntryDTO;
  initialScope?: ConfigScope;
  vpsList: VpsDTO[];
  targets: TargetDTO[];
  onClose: () => void;
}) {
  const toast = useToast();
  const id = useId();
  const env = cfg.environment;
  const [name, setName] = useState(entry?.name ?? '');
  const [mode, setMode] = useState<'literal' | 'shared'>('literal');
  const [value, setValue] = useState('');
  const [empty, setEmpty] = useState(false);
  const [secret, setSecret] = useState(entry?.secret ?? true);
  const [show, setShow] = useState(false);
  const [multiline, setMultiline] = useState(false);
  const [scope, setScope] = useState<ConfigScope>(initialScope ?? { kind: 'environment' });
  const [sharedId, setSharedId] = useState('');
  const shared = useSharedValues(mode === 'shared');

  const declared = entry?.declared ?? false;
  const isSecret = declared ? entry!.secret : secret;
  const nameValid = VAR_NAME_RE.test(name);
  const eligibleShared = (shared.data ?? []).filter((s) => (s.allowedEnvironments.includes(env) || s.allowedEnvironments.includes('*')) && (!cfg.protected || s.allowProtected));

  const save = useApiMutation((body: SetConfigRequest) => api.put(`/api/v1/projects/${seg(projectKey)}/environments/${seg(env)}/config/${seg(name)}`, body), {
    invalidate: [qk.config(projectKey, env), qk.environments(projectKey), qk.targets(projectKey), ['target']],
  });

  const canSubmit = nameValid && scopeValid(scope) && (mode === 'shared' ? Boolean(sharedId) : empty || value.length > 0) && !save.isPending;

  const submit = () => {
    const body: SetConfigRequest = { scope };
    if (mode === 'shared') body.sharedValueId = sharedId;
    else {
      body.value = empty ? '' : value;
      if (!declared) body.secret = secret;
    }
    save.mutate(body, {
      onSuccess: () => {
        toast.success(`${name} saved`, { description: 'Running targets keep their applied configuration until you apply the change from the target page.' });
        onClose();
      },
      onError: () => haptic('error'),
    });
  };

  return (
    <Modal title={entry ? `Set ${entry.name}` : 'Add a variable'} onClose={onClose} size="lg">
      <form
        className="space-y-4"
        onInvalidCapture={() => haptic('error')}
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit();
          else haptic('error');
        }}
      >
        <p className="text-sm text-base-content/65">
          Environment <strong>{env}</strong>
          {cfg.protected ? ' (protected)' : ''}.{' '}
          {entry?.description ?? (declared ? '' : 'Variables not declared in devrobot.yaml are delivered to the application service only.')}
          {entry?.services.length ? ` Delivered to: ${entry.services.join(', ')}.` : ''}
        </p>
        {!entry ? (
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Name</legend>
            <input className={`input w-full font-mono ${name && !nameValid ? 'input-error' : ''}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="API_TOKEN" autoCapitalize="characters" spellCheck={false} required />
          </fieldset>
        ) : null}

        <ScopePicker env={env} value={scope} onChange={setScope} vpsList={vpsList} targets={targets} />

        <div role="radiogroup" aria-label="Value source" className="inline-flex gap-0.5 rounded-field border border-hairline bg-base-200/70 p-1">
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'literal'}
            className={`btn btn-sm border-0 shadow-none ${mode === 'literal' ? 'bg-base-100 dr-shadow-xs ring-1 ring-hairline' : 'btn-ghost text-base-content/60'}`}
            onClick={() => setMode('literal')}
          >
            Enter a value
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'shared'}
            className={`btn btn-sm border-0 shadow-none ${mode === 'shared' ? 'bg-base-100 dr-shadow-xs ring-1 ring-hairline' : 'btn-ghost text-base-content/60'}`}
            onClick={() => setMode('shared')}
          >
            Bind a shared value
          </button>
        </div>

        {mode === 'literal' ? (
          <div key="literal" className="animate-rise space-y-3">
            {!declared ? (
              <label className="flex cursor-pointer items-start gap-3">
                <input type="checkbox" className="toggle toggle-primary mt-0.5" checked={secret} onChange={(e) => setSecret(e.target.checked)} />
                <span>
                  <span className="font-medium">Secret</span>
                  <span className="block text-sm text-base-content/60">Masked everywhere; stored encrypted; revealing it is audited. Declared variables use the manifest classification.</span>
                </span>
              </label>
            ) : isSecret ? (
              <p className="flex items-center gap-2 text-sm text-base-content/65">
                <Lock className="size-4" aria-hidden="true" /> Declared secret in the manifest: stored encrypted and never shown after saving.
              </p>
            ) : null}
            <fieldset className="fieldset" disabled={empty}>
              <legend className="fieldset-legend">Value</legend>
              {multiline || !isSecret ? (
                <textarea
                  id={`${id}-v`}
                  className="textarea w-full font-mono"
                  rows={multiline ? 6 : 2}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  style={isSecret && !show ? ({ WebkitTextSecurity: 'disc' } as React.CSSProperties) : undefined}
                />
              ) : (
                <label className="input w-full">
                  <input id={`${id}-v`} type={show ? 'text' : 'password'} className="grow font-mono" value={value} onChange={(e) => setValue(e.target.value)} autoComplete="new-password" spellCheck={false} />
                  <button type="button" className="btn btn-ghost btn-xs btn-square" onClick={() => setShow(!show)} aria-label={show ? 'Hide value' : 'Show value while typing'}>
                    {show ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
                  </button>
                </label>
              )}
              <div className="flex flex-wrap gap-4">
                {isSecret ? (
                  <label className="label cursor-pointer gap-2">
                    <input type="checkbox" className="checkbox checkbox-xs" checked={multiline} onChange={(e) => setMultiline(e.target.checked)} />
                    Multi-line value
                  </label>
                ) : null}
              </div>
            </fieldset>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" className="checkbox checkbox-sm" checked={empty} onChange={(e) => setEmpty(e.target.checked)} />
              Intentionally empty (deliver an empty string, not “missing”)
            </label>
          </div>
        ) : (
          <fieldset key="shared" className="fieldset animate-rise">
            <legend className="fieldset-legend">Shared value</legend>
            {shared.isPending ? <div className="skeleton h-10 w-full" /> : null}
            <ErrorAlert error={shared.error} />
            {shared.data ? (
              eligibleShared.length === 0 ? (
                <p className="text-sm text-base-content/65">No shared value is allowed in {env}{cfg.protected ? ' (protected environments need a value that explicitly allows them)' : ''}.</p>
              ) : (
                <select className="select w-full" value={sharedId} onChange={(e) => setSharedId(e.target.value)}>
                  <option value="">Choose…</option>
                  {eligibleShared.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} (v{s.version}
                      {s.secret ? ', secret' : ''}){s.description ? ` — ${s.description}` : ''}
                    </option>
                  ))}
                </select>
              )
            ) : null}
            <p className="label whitespace-normal">The binding follows the shared value: when an administrator changes it, bound targets show pending configuration.</p>
          </fieldset>
        )}

        <ErrorAlert error={save.error} title="The value was not saved" />
        <div className="modal-action">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <Haptic disabled={!canSubmit}>
            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
              {save.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : null}
              Save
            </button>
          </Haptic>
        </div>
      </form>
    </Modal>
  );
}
